import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { PlatformModeSwitch } from '@/components/PlatformModeSwitch';
import { BoundedJobsCard } from '@/components/BoundedJobsCard';
import { Activity, Database, Bot, Gauge, DollarSign, AlertTriangle, CheckCircle2, RefreshCw, Clock, HardDriveDownload, ShieldAlert, ServerCog } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };
const SEV = { CRITICAL: C.red, HIGH: C.red, MEDIUM: C.amber, LOW: C.slate };
const OVERALL = { healthy: C.green, degraded: C.amber, down: C.red };

const Stat = ({ icon: Icon, label, value, sub, color, testid }) => (
  <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid={testid}>
    <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4" style={{ color: color || C.blue }} /><span className="text-xs uppercase tracking-wider" style={{ color: C.slate }}>{label}</span></div>
    <div className="text-2xl font-bold font-mono" style={{ color: C.navy }}>{value}</div>
    {sub && <div className="text-[11px] mt-0.5" style={{ color: C.slate }}>{sub}</div>}
  </Card>
);

const PHASE_TONE = { ok: C.green, partial: C.amber, skipped: C.slate, failed: C.red };

function StartupReportCard({ report }) {
  const tone = PHASE_TONE[report.status] || C.slate;
  const failed = report.phases.filter(p => p.status === 'failed').length;
  const partial = report.phases.filter(p => p.status === 'partial').length;
  return (
    <Card className="p-4 rounded-md" style={{ border: `1px solid ${tone}30` }} data-testid="startup-report-card">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <ServerCog className="w-4 h-4" style={{ color: tone }} />
        <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Startup Report</h3>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase" style={{ background: `${tone}1A`, color: tone }} data-testid="startup-report-status">{report.status}</span>
        <span className="text-[11px] ml-auto" style={{ color: C.slate }}>
          {report.phases.length} phases · {failed} failed · {partial} partial
          {report.started_at && <> · booted {new Date(report.started_at).toLocaleString('en-AU')}</>}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
        {report.phases.map((p, i) => {
          const pc = PHASE_TONE[p.status] || C.slate;
          return (
            <div key={i} className="flex items-start gap-2 text-xs" data-testid={`startup-phase-${p.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
              <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: pc }} />
              <span className="font-mono font-semibold whitespace-nowrap" style={{ color: C.navy }}>{p.name}</span>
              <span className="font-bold uppercase text-[9px] mt-0.5 shrink-0" style={{ color: pc }}>{p.status}</span>
              {p.detail && <span className="truncate" style={{ color: C.slate }} title={p.detail}>{p.detail}</span>}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function SystemHealthPage() {
  const api = useApi();
  const [h, setH] = useState(null);
  const [backup, setBackup] = useState(null);
  const [startup, setStartup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);
  const timer = useRef(null);

  const load = useCallback(() => {
    return Promise.all([
      api.get('/monitoring/overview'),
      api.get('/admin/backup/status').catch(() => ({ data: null })),
      api.get('/platform/startup-report').catch(() => ({ data: null })),
    ]).then(([m, b, s]) => { setH(m.data); setBackup(b.data); setStartup(s.data); }).catch(e => console.error(e?.message)).finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 15000);   // live auto-refresh
    return () => clearInterval(timer.current);
  }, [load]);

  const runCheck = () => {
    setBusy(true);
    api.post('/monitoring/check').then(r => { setH(r.data); toast.success('Health check recorded'); }).catch(() => toast.error('Check failed')).finally(() => setBusy(false));
  };

  const runBackup = () => {
    setBackupBusy(true);
    api.post('/admin/backup/run').then(() => { toast.success('Backup completed'); load(); }).catch(e => toast.error(e?.response?.data?.detail || 'Backup failed')).finally(() => setBackupBusy(false));
  };

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-md" />)}</div>;
  if (!h) return <div className="text-sm" style={{ color: C.slate }}>Unable to load system health.</div>;

  const api_ = h.api || {};
  const db = h.database || {};
  const w = h.worker || {};
  const spend = h.llm_spend || {};
  const oc = OVERALL[h.overall_status] || C.slate;

  return (
    <div className="space-y-5" data-testid="system-health-page">
      <PlatformModeSwitch />
      <BoundedJobsCard />
      {/* Overall banner */}
      <Card className="p-5 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" style={{ background: `${oc}10`, border: `1px solid ${oc}40` }} data-testid="health-banner">
        <div className="flex items-center gap-3">
          {h.overall_status === 'healthy' ? <CheckCircle2 className="w-7 h-7" style={{ color: oc }} /> : <AlertTriangle className="w-7 h-7" style={{ color: oc }} />}
          <div>
            <div className="text-lg font-bold capitalize" style={{ color: oc }} data-testid="overall-status">{h.overall_status}</div>
            <div className="text-xs" style={{ color: C.slate }}>Uptime {h.uptime?.human} · {h.alerts?.length || 0} active alert(s) · checked {new Date(h.checked_at).toLocaleTimeString()}</div>
          </div>
        </div>
        <Button size="sm" onClick={runCheck} disabled={busy} data-testid="run-check-btn" style={{ background: C.blue, color: 'white' }}>
          {busy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Run Check
        </Button>
      </Card>

      {/* Alerts */}
      {h.alerts?.length > 0 && (
        <Card className="p-4 rounded-md" style={{ border: `1px solid ${C.red}30` }} data-testid="alerts-panel">
          <h3 className="text-sm font-semibold mb-2" style={{ color: C.navy }}>Active Alerts</h3>
          <div className="space-y-2">
            {h.alerts.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-sm" data-testid={`alert-${a.metric}`}>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded shrink-0" style={{ background: `${SEV[a.severity]}1A`, color: SEV[a.severity] }}>{a.severity}</span>
                <span style={{ color: C.navy }}>{a.message}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Startup report */}
      {startup?.phases?.length > 0 && <StartupReportCard report={startup} />}

      {/* Signal grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Database} label="Database" value={db.status === 'up' ? `${db.ping_ms}ms` : 'DOWN'} sub={db.status === 'up' ? 'MongoDB ping' : db.error} color={db.status === 'up' ? C.green : C.red} testid="stat-db" />
        <Stat icon={Bot} label="Autonomous Worker" value={w.status} sub={w.staleness_sec != null ? `last beat ${w.staleness_sec}s ago` : 'awaiting first beat'} color={w.status === 'healthy' ? C.green : w.status === 'stale' ? C.red : C.slate} testid="stat-worker" />
        <Stat icon={Activity} label="Requests" value={api_.total_requests?.toLocaleString()} sub={`${api_.error_rate_pct}% 5xx · ${api_.client_error_rate_pct}% 4xx`} color={api_.error_rate_pct > 2 ? C.red : C.green} testid="stat-requests" />
        <Stat icon={Gauge} label="Latency p95" value={`${api_.latency_p95_ms}ms`} sub={`p50 ${api_.latency_p50_ms}ms · avg ${api_.latency_avg_ms}ms`} color={api_.latency_p95_ms > 1500 ? C.red : C.green} testid="stat-latency" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Status class breakdown */}
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="status-breakdown">
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Response Status Mix</h3>
          {['2xx', '3xx', '4xx', '5xx'].map(cls => {
            const n = api_.by_status_class?.[cls] || 0;
            const pct = api_.total_requests ? (100 * n / api_.total_requests) : 0;
            const col = cls === '5xx' ? C.red : cls === '4xx' ? C.amber : cls === '2xx' ? C.green : C.slate;
            return (
              <div key={cls} className="mb-2" data-testid={`status-${cls}`}>
                <div className="flex justify-between text-xs mb-1"><span style={{ color: C.navy }}>{cls}</span><span style={{ color: C.slate }}>{n} · {pct.toFixed(1)}%</span></div>
                <div className="h-1.5 rounded-full" style={{ background: '#EEF1F5' }}><div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: col }} /></div>
              </div>
            );
          })}
        </Card>

        {/* LLM spend */}
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="llm-spend">
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: C.navy }}><DollarSign className="w-3.5 h-3.5" style={{ color: C.green }} />LLM Spend (session)</h3>
          <div className="flex gap-6">
            <div><div className="text-2xl font-bold font-mono" style={{ color: C.navy }}>${(spend.total_cost || 0).toFixed(4)}</div><div className="text-[11px]" style={{ color: C.slate }}>total cost</div></div>
            <div><div className="text-2xl font-bold font-mono" style={{ color: C.navy }}>{(spend.total_calls || 0).toLocaleString()}</div><div className="text-[11px]" style={{ color: C.slate }}>calls</div></div>
            <div><div className="text-2xl font-bold font-mono" style={{ color: C.navy }}>{(spend.total_tokens || 0).toLocaleString()}</div><div className="text-[11px]" style={{ color: C.slate }}>tokens</div></div>
          </div>
          {h.recent_5xx?.length === 0 && <p className="text-[11px] mt-3 flex items-center gap-1" style={{ color: C.slate }}><Clock className="w-3 h-3" />Spend resets on process restart.</p>}
        </Card>
      </div>

      {/* Recent 5xx */}
      {api_.recent_5xx?.length > 0 && (
        <Card className="p-4 rounded-md" style={{ border: `1px solid ${C.red}30` }} data-testid="recent-5xx">
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.navy }}>Recent Server Errors (5xx)</h3>
          <div className="space-y-1">
            {api_.recent_5xx.map((e, i) => (
              <div key={i} className="flex justify-between text-xs font-mono" style={{ color: C.slate }}>
                <span style={{ color: C.red }}>{e.status} {e.path}</span><span>{new Date(e.at).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
      {/* Backup & DR */}
      <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="backup-dr-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-2" style={{ color: C.navy }}><HardDriveDownload className="w-3.5 h-3.5" style={{ color: C.blue }} />Backup & Disaster Recovery</h3>
          <Button size="sm" onClick={runBackup} disabled={backupBusy} data-testid="run-backup-btn" style={{ background: C.blue, color: 'white' }}>
            {backupBusy ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <HardDriveDownload className="w-4 h-4 mr-1.5" />}Run Backup
          </Button>
        </div>
        {backup ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="backup-stats">
            <div>
              <div className="flex items-center gap-1.5">
                {backup.within_rpo ? <CheckCircle2 className="w-4 h-4" style={{ color: C.green }} /> : <ShieldAlert className="w-4 h-4" style={{ color: C.amber }} />}
                <span className="text-sm font-bold" style={{ color: backup.within_rpo ? C.green : C.amber }} data-testid="rpo-status">{backup.within_rpo ? 'Within RPO' : 'RPO breach'}</span>
              </div>
              <div className="text-[11px] mt-0.5" style={{ color: C.slate }}>RTO {backup.config?.rto_hours}h · RPO {backup.config?.rpo_hours}h</div>
            </div>
            <div><div className="text-lg font-bold font-mono" style={{ color: C.navy }}>{backup.last_backup_age_hours != null ? `${backup.last_backup_age_hours}h` : '—'}</div><div className="text-[11px]" style={{ color: C.slate }}>since last backup</div></div>
            <div><div className="text-lg font-bold font-mono" style={{ color: C.navy }}>{backup.last_backup?.size_mb != null ? `${backup.last_backup.size_mb} MB` : '—'}</div><div className="text-[11px]" style={{ color: C.slate }}>last archive size</div></div>
            <div><div className="text-lg font-bold font-mono" style={{ color: C.navy }}>{backup.counts?.success || 0}<span className="text-xs" style={{ color: C.slate }}> ok</span> {backup.counts?.failed ? <span style={{ color: C.red }}>· {backup.counts.failed} fail</span> : null}</div><div className="text-[11px]" style={{ color: C.slate }}>backup runs</div></div>
          </div>
        ) : <p className="text-sm" style={{ color: C.slate }}>No backups yet — run your first operational backup.</p>}
      </Card>
    </div>
  );
}
